import { supabase } from "@/integrations/supabase/client";

export interface StorefrontProduct {
  id: string;
  name: string;
  price: number;
  description: string;
  sku: string;
  category: string;
  image: string; // The primary image emoji or URL
  images: string[];
  stock: number;
}

export interface StorefrontProductsResult {
  products: StorefrontProduct[];
  total: number;
}

export const fetchStorefrontProducts = async (
  organizationId: string,
  page = 1,
  pageSize = 20,
  _options?: { showOutOfStock?: boolean },
): Promise<StorefrontProductsResult> => {
  if (!organizationId) return { products: [], total: 0 };

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const query = supabase
    .from('ecommerce_products')
    .select(`
      id,
      name,
      price,
      description,
      sku,
      category:ecommerce_categories(name),
      images:ecommerce_product_images(image_url, is_primary),
      inventory:ecommerce_inventory(quantity, reserved_quantity)
    `, { count: 'exact' })
    .eq('organization_id', organizationId)
    .eq('status', 'active')
    .eq('is_published', true)
    .eq('is_deleted', false);

  const { data: products, error: productsError, count } = await query.range(from, to);

  if (productsError) {
    console.error("Error fetching products:", productsError);
    return { products: [], total: 0 };
  }

  const mapped = (products ?? []).map((p: any) => {
    const primaryImage = p.images?.find((img: any) => img.is_primary)?.image_url || 
                        p.images?.[0]?.image_url || 
                        "📦";

    const totalQty = (p.inventory ?? []).reduce((sum: number, inv: any) => sum + (Number(inv.quantity) || 0), 0);
    const totalReserved = (p.inventory ?? []).reduce((sum: number, inv: any) => sum + (Number(inv.reserved_quantity) || 0), 0);
    const available = Math.max(0, totalQty - totalReserved);

    return {
      id: p.id,
      name: p.name,
      price: Number(p.price) || 0,
      description: p.description || "",
      sku: p.sku,
      category: p.category?.name || "Uncategorized",
      image: primaryImage,
      images: p.images?.map((img: any) => img.image_url) || [],
      stock: available,
    };
  });

  return { products: mapped, total: count ?? 0 };
};

export const fetchStorefrontCategories = async (organizationId: string): Promise<string[]> => {
  if (!organizationId) return [];

  const { data, error } = await supabase
    .from('ecommerce_categories')
    .select('name, ecommerce_products!inner(id)')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .eq('ecommerce_products.is_published', true)
    .eq('ecommerce_products.status', 'active')
    .eq('ecommerce_products.is_deleted', false);

  if (error) {
    console.error("Error fetching categories:", error);
    return [];
  }

  return data.map(c => c.name);
};

/** Returns available quantity for a product (for add-to-cart stock check). Uses RPC with JWT org. */
export const fetchProductAvailability = async (
  organizationId: string,
  productId: string,
): Promise<number> => {
  if (!organizationId || !productId) return 0;
  const { data, error } = await supabase.rpc('get_ecommerce_product_availability', {
    p_organization_id: organizationId,
    p_product_id: productId,
  });
  if (error) {
    console.error("Error fetching product availability:", error);
    return 0;
  }
  return typeof data === 'number' ? data : 0;
};
